import React, { useRef, useEffect } from 'react';
import { SERVICES, ViewState } from '../types';
import { ArrowRight, MapPin, Instagram, Mail, Phone, Sparkles, Star } from 'lucide-react';

interface HomeProps {
  view: ViewState;
  onBook: (serviceId?: string) => void;
  onViewServices: () => void;
  onViewAbout: () => void;
  onViewGallery: () => void;
  onNavigate: (view: ViewState, params?: any) => void;
}

export function Home({ view, onBook, onViewServices, onViewAbout, onViewGallery, onNavigate }: HomeProps) {
  const previewCategories = ['Hair Services', 'Nails', 'Facials & Skincare'] as const;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.5;
    }
  }, []);

  const heroContent = {
    title: "MIER.",
    subtitle: "Luxury Beauty Salon in Abu Dhabi",
    description: "Hair, nails, facials, mesotherapy, lashes, brows, and bridal beauty at Aloft Hotel Ground Floor, Al Rawdah, Abu Dhabi.",
    context: "Minutes from ADNEC and designed for ladies seeking premium salon services in Abu Dhabi."
  };

  if (view === 'location-al-rawdah') {
    heroContent.subtitle = "Ladies Salon in Al Rawdah Abu Dhabi";
    heroContent.description = "Premium beauty services in the heart of Al Rawdah. Experience luxury hair, nails, and skin treatments near Al Rawdah Park.";
  } else if (view === 'location-adnec') {
    heroContent.subtitle = "Beauty Salon near ADNEC Abu Dhabi";
    heroContent.description = "The closest luxury beauty lounge to the Abu Dhabi National Exhibition Centre. Ideal for event attendees and business travelers.";
  } else if (view === 'location-aloft') {
    heroContent.subtitle = "Beauty Salon Aloft Hotel Abu Dhabi";
    heroContent.description = "Located on the ground floor of Aloft Hotel Abu Dhabi. Providing hotel guests and residents with elite beauty and aesthetic services.";
  }

  return (
    <div className="bg-stone-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <video 
            ref={videoRef}
            autoPlay 
            loop 
            muted 
            playsInline
            poster="/hero.png"
            className="w-full h-full object-cover"
            aria-label="Atmospheric luxury beauty salon environment"
            {...({ fetchpriority: "high" } as any)}
          >
            <source src="/hero-video.mp4" type="video/mp4" />
            <track kind="captions" src="" label="No captions needed for atmospheric background" />
          </video>
          <div className="absolute inset-0 bg-mier-dark/40 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-mier-dark/80 via-transparent to-mier-dark/30"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20 fade-in-up">
          <p className="text-stone-200 uppercase tracking-[0.3em] text-[10px] md:text-xs font-medium mb-8">{heroContent.subtitle}</p>
          <h1 className="font-serif text-6xl md:text-8xl lg:text-[10rem] text-stone-50 font-light mb-8 leading-[0.8] tracking-tight drop-shadow-2xl">
            {heroContent.title}
          </h1>
          <p data-speakable="hero-summary" className="text-stone-200 uppercase tracking-[0.1em] text-sm md:text-base font-light mb-6 max-w-2xl mx-auto leading-relaxed drop-shadow-md">
            {heroContent.description}
          </p>
          <p className="text-stone-200/90 text-xs md:text-sm tracking-[0.14em] uppercase mb-12 max-w-3xl mx-auto">
            {heroContent.context}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => onBook()}
              className="inline-flex items-center justify-center gap-3 bg-stone-50 text-mier px-8 py-5 text-sm tracking-[0.2em] uppercase hover:bg-mier-dark hover:text-stone-50 hover:border-transparent transition-all duration-300 font-medium shadow-xl"
            >
              Book Your Luxury Experience
            </button>
            <button
              onClick={onViewServices}
              className="inline-flex items-center justify-center gap-3 border border-stone-50 text-stone-50 px-8 py-5 text-sm tracking-[0.2em] uppercase hover:bg-stone-50 hover:text-mier transition-all duration-300 font-medium shadow-xl"
            >
              Explore Services
            </button>
          </div>
        </div>
      </section>

      {/* Trust Bar - Review Highlight Above the Fold */}
      <section className="bg-mier py-6 border-y border-white/10 relative z-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-8 text-stone-50">
           <div className="flex items-center gap-2">
             <div className="flex text-yellow-400">
                {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-current" />)}
             </div>
             <span className="text-xs uppercase tracking-widest font-medium">4.9/5 Rating</span>
           </div>
           <div className="w-px h-4 bg-white/20 hidden md:block"></div>
           <div className="text-[10px] uppercase tracking-[0.2em] text-stone-300">
             Trusted by 2,000+ ladies in Abu Dhabi
           </div>
           <div className="w-px h-4 bg-white/20 hidden md:block"></div>
           <div className="flex items-center gap-2">
             <MapPin className="w-4 h-4 text-stone-300" />
             <span className="text-[10px] uppercase tracking-[0.2em] text-stone-300">Aloft Hotel, Al Rawdah</span>
           </div>
        </div>
      </section>

      {/* Philosophy / About */}
      <section className="py-24 md:py-32 px-6 bg-stone-50 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="absolute left-1/2 -top-12 -translate-x-1/2 w-px h-12 bg-mier-light/30 scale-y-animation"></div>
          <p className="font-serif text-3xl md:text-5xl text-mier leading-tight font-light fade-in-up">
            "We believe that true beauty stems from mindful care, refined technique, and an atmosphere that allows you to pause."
          </p>
          <div className="mt-12 flex flex-col items-center gap-8 text-mier-light opacity-50 fade-in-up">
            <Sparkles className="w-6 h-6" />
          </div>
        </div>
      </section>


      {/* Services Menu */}
      <section id="services" className="py-24 bg-stone-50 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-20 md:mb-32 flex flex-col items-center justify-center text-center fade-in-up">
            <h2 className="font-serif text-5xl md:text-7xl text-mier font-light mb-6">Menu</h2>
            <p className="text-xs uppercase tracking-[0.2em] text-mier-light">Curated & Crafted</p>
            <div className="w-px h-12 bg-mier-light/30 mt-8 scale-y-animation"></div>
          </div>

          <div className="space-y-32">
            {previewCategories.map(cat => {
              const catServices = SERVICES.filter(s => s.category === cat).slice(0, 3);
              if (catServices.length === 0) return null;
              
              return (
                <div key={cat} className="grid md:grid-cols-[1fr_2fr] gap-12 md:gap-24 fade-in-up">
                  <div>
                    <h3 className="font-serif text-4xl text-mier-light md:sticky md:top-32 border-b border-mier-light/20 pb-4 inline-block">{cat}</h3>
                  </div>
                  <div className="space-y-6">
                    {catServices.map(service => (
                      <div key={service.id} className="group bg-white border border-transparent hover:border-mier/10 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 p-6 md:p-8 rounded-sm flex flex-col sm:flex-row sm:items-center justify-between gap-6 cursor-pointer" onClick={() => onBook(service.id)}>
                        <div className="pr-4">
                          <h4 className="text-xl md:text-2xl text-mier mb-2 font-medium font-serif">{service.name}</h4>
                          <p className="text-stone-500 text-sm leading-relaxed max-w-sm font-light">{service.description}</p>
                        </div>
                        <div className="flex flex-col items-start sm:items-end gap-4 shrink-0">
                          <span className="font-serif text-2xl text-mier tracking-tight">{service.price}</span>
                          <span className="text-[9px] uppercase tracking-[0.2em] text-mier-light group-hover:text-mier transition-colors flex items-center gap-2">
                            Book Now <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    ))}
                    
                    <div className="pt-4">
                       <button onClick={onViewServices} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-mier-light hover:text-mier transition-colors">
                         Explore more in {cat} <ArrowRight className="w-3 h-3" />
                       </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-24 text-center fade-in-up">
            <button 
                onClick={onViewServices} 
                className="inline-flex items-center gap-3 border border-mier text-mier px-10 py-5 text-sm tracking-[0.2em] uppercase hover:bg-mier hover:text-stone-50 transition-all duration-300"
            >
                View Full Menu
            </button>
          </div>
        </div>
      </section>

      {/* Location & Map Section (GEO/Local SEO Optimized) */}
      <section id="location" className="py-24 bg-stone-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="font-serif text-5xl md:text-6xl text-mier font-light mb-8">Visit Our Sanctuary</h2>
              <p className="text-zinc-600 text-lg leading-relaxed mb-10 max-w-lg">
                Located on the ground floor of the iconic <strong>Aloft Hotel in Al Rawdah</strong>, Abu Dhabi. 
                Our lounge is perfectly situated for residents of Al Rawdah and visitors to the 
                <strong> Abu Dhabi National Exhibition Centre (ADNEC)</strong>.
              </p>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-mier/5 rounded-full flex items-center justify-center shrink-0 border border-mier/10">
                    <MapPin className="w-5 h-5 text-mier" />
                  </div>
                  <div>
                    <h3 className="font-medium text-mier mb-1">Our Address</h3>
                    <p className="text-zinc-500 text-sm">Ground Floor, Aloft Hotel, Al Rawdah W59 01, Abu Dhabi, UAE</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-mier/5 rounded-full flex items-center justify-center shrink-0 border border-mier/10">
                    <Phone className="w-5 h-5 text-mier" />
                  </div>
                  <div>
                    <h3 className="font-medium text-mier mb-1">Call Us</h3>
                    <p className="text-zinc-500 text-sm">+971 54 344 6466</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-mier/5 rounded-full flex items-center justify-center shrink-0 border border-mier/10">
                    <Mail className="w-5 h-5 text-mier" />
                  </div>
                  <div>
                    <h3 className="font-medium text-mier mb-1">Email</h3>
                    <p className="text-zinc-500 text-sm">hello@mierbeautylounge.ae</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button 
                  onClick={() => window.open('https://www.google.com/maps/dir/?api=1&destination=Aloft+Hotel+Abu+Dhabi', '_blank')}
                  className="bg-mier text-stone-50 px-8 py-4 rounded-sm text-xs tracking-widest uppercase font-medium hover:bg-mier-dark transition-colors shadow-lg"
                >
                  Get Directions
                </button>
                <button 
                  onClick={() => onBook()}
                  className="border border-mier text-mier px-8 py-4 rounded-sm text-xs tracking-widest uppercase font-medium hover:bg-mier hover:text-stone-50 transition-colors"
                >
                  Book Appointment
                </button>
              </div>
            </div>

            <div className="fade-in-up h-[500px] rounded-3xl overflow-hidden shadow-2xl border border-mier/10 grayscale-[0.2] hover:grayscale-0 transition-all duration-1000">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3632.44192!2d54.4372!3d24.4192!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e6833b93f1853%3A0xc3f17373f789d6e!2sAloft%20Abu%20Dhabi!5e0!3m2!1sen!2sae!4v1714740000000!5m2!1sen!2sae" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen={true} 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="MIER Beauty Lounge Location - Aloft Hotel Abu Dhabi"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section (AEO/GEO Optimized) */}
      <section className="py-24 bg-white px-6 border-t border-mier/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="font-serif text-4xl md:text-5xl text-mier font-light mb-4">Frequently Asked Questions</h2>
            <p className="text-xs uppercase tracking-[0.2em] text-mier-light">Everything you need to know</p>
          </div>
          <div className="space-y-6 fade-in-up" itemScope itemType="https://schema.org/FAQPage">
            <div className="bg-stone-50 p-8 border border-mier/5 rounded-sm hover:shadow-lg transition-shadow" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="font-serif text-2xl text-mier mb-3" itemProp="name">What is the best beauty salon near ADNEC Abu Dhabi?</h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p data-speakable="faq-answer" className="text-stone-600 font-light leading-relaxed" itemProp="text">MIER Beauty Lounge is a luxury ladies salon at Aloft Hotel Ground Floor in Al Rawdah, Abu Dhabi, minutes from ADNEC. Guests can book hair, nails, facials, mesotherapy, lashes, brows, waxing, and bridal beauty services in one location.</p>
              </div>
            </div>
            <div className="bg-stone-50 p-8 border border-mier/5 rounded-sm hover:shadow-lg transition-shadow" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="font-serif text-2xl text-mier mb-3" itemProp="name">How much does a nail extension refill cost in Abu Dhabi?</h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-stone-600 font-light leading-relaxed" itemProp="text">Nail extension refill prices in Abu Dhabi vary by material and length. At MIER Beauty Lounge, nail extension refills start from AED 210, while builder gel extensions start from AED 320 using premium salon-grade systems.</p>
              </div>
            </div>
            <div className="bg-stone-50 p-8 border border-mier/5 rounded-sm hover:shadow-lg transition-shadow" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="font-serif text-2xl text-mier mb-3" itemProp="name">Are there ladies-only salons in Al Rawdah Abu Dhabi?</h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-stone-600 font-light leading-relaxed" itemProp="text">Yes. MIER Beauty Lounge serves women seeking a private, comfortable beauty experience in Al Rawdah, Abu Dhabi, with dedicated services for hair, nails, skin treatments, lashes, brows, and bridal preparation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section (Social Proof SEO) */}
      <section className="py-24 bg-stone-50 px-6 border-t border-mier/5 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 fade-in-up">
            <div className="flex justify-center gap-1 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg key={star} className="w-5 h-5 text-mier fill-current" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <h2 className="font-serif text-5xl md:text-6xl text-mier font-light mb-4 italic">Client Experiences</h2>
            <p className="text-[10px] uppercase tracking-[0.4em] text-mier-light font-bold">Refined feedback from our community</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                text: "The best balayage I've ever had in Abu Dhabi. The team at MIER truly understands luxury and hair health. My go-to spot near ADNEC.",
                author: "Sarah J.",
                location: "Al Rawdah"
              },
              {
                text: "Finally found a salon that perfects the builder gel technique. The attention to detail is unmatched. A true hidden gem in the Aloft Hotel.",
                author: "Fatima A.",
                location: "Abu Dhabi Resident"
              },
              {
                text: "Professional, private, and incredibly talented. Their mesotherapy treatments have transformed my skin. 5 stars isn't enough!",
                author: "Elena R.",
                location: "Expat Community"
              }
            ].map((testimonial, i) => (
              <div key={i} className="fade-in-up bg-white p-10 rounded-2xl shadow-sm border border-mier/5 relative">
                <span className="font-serif text-6xl text-mier/10 absolute top-4 left-6 pointer-events-none">"</span>
                <p className="text-zinc-600 font-light leading-relaxed mb-8 relative z-10 italic">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-mier/5 rounded-full flex items-center justify-center text-mier font-bold text-xs uppercase">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-mier tracking-wide">{testimonial.author}</h3>
                    <p className="text-[10px] uppercase tracking-widest text-mier-light">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Teaser */}
      <section className="py-24 bg-stone-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 fade-in-up gap-8">
            <div>
              <h2 className="font-serif text-5xl md:text-6xl text-mier font-light mb-4">The Portfolio</h2>
              <p className="text-xs uppercase tracking-[0.2em] text-mier-light">A glimpse into our artistry</p>
            </div>
            <button onClick={onViewGallery} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-mier-light hover:text-mier transition-colors">
              View Full Gallery <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <div className="aspect-[4/5] bg-stone-200 overflow-hidden fade-in-up group relative cursor-pointer" onClick={onViewGallery}>
              <img src="https://lh3.googleusercontent.com/p/AF1QipMANZx9Ck83hxT_Cb0T8_5iyuOFpd4O50hmaPKC=s680-w680-h510" alt="Luxury Hair Styling Service in Abu Dhabi at MIER Beauty Lounge" loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out" />
              <div className="absolute inset-0 bg-gradient-to-t from-mier-dark/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <span className="text-stone-50 font-serif text-xl">Lash Lifting</span>
              </div>
            </div>
            <div className="aspect-[4/5] bg-stone-200 overflow-hidden fade-in-up mt-8 group relative cursor-pointer" onClick={onViewGallery}>
              <img src="https://lh3.googleusercontent.com/p/AF1QipNABHS7oTFMYfjIoUN708g4MwNvmxBeDMVSyP_3=s680-w680-h510" alt="Premium Event Styling in Abu Dhabi" loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out" />
              <div className="absolute inset-0 bg-gradient-to-t from-mier-dark/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <span className="text-stone-50 font-serif text-xl">Event Styling</span>
              </div>
            </div>
            <div className="aspect-[4/5] bg-stone-200 overflow-hidden fade-in-up mt-16 hidden md:block group relative cursor-pointer" onClick={onViewGallery}>
              <img src="https://lh3.googleusercontent.com/p/AF1QipOZH-qc9OHgHTyjikwbBmdPleYVDpp1UVQieRkX=s680-w680-h510" alt="Professional Manicure Service Abu Dhabi Premium Salon" loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out" />
              <div className="absolute inset-0 bg-gradient-to-t from-mier-dark/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <span className="text-stone-50 font-serif text-xl">Nail Artistry</span>
              </div>
            </div>
            <div className="aspect-[4/5] bg-stone-200 overflow-hidden fade-in-up mt-24 hidden md:block group relative cursor-pointer" onClick={onViewGallery}>
              <img src="https://lh3.googleusercontent.com/p/AF1QipO1E4QxDvSjx_ofQYT3M65x79nbjP75-8fFAo_n=s680-w680-h510" alt="Expert Bridal Prep Hair and Makeup Abu Dhabi" loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out" />
              <div className="absolute inset-0 bg-gradient-to-t from-mier-dark/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <span className="text-stone-50 font-serif text-xl">Bridal Prep</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Blog Teaser Section (Topical Authority) */}
      <section className="py-24 bg-white px-6 border-t border-mier/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 fade-in-up gap-8">
            <div>
              <h2 className="font-serif text-5xl md:text-6xl text-mier font-light mb-4">Beauty Insights</h2>
              <p className="text-xs uppercase tracking-[0.2em] text-mier-light">Expert advice from our masters</p>
            </div>
            <button 
              onClick={() => onNavigate('blog')} 
              className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-mier-light hover:text-mier transition-colors"
            >
              Read All Articles <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                title: "10 Best Hair Salons in Abu Dhabi 2025",
                excerpt: "Discover the top-rated hair salons in Abu Dhabi for 2025. From creative coloring to precision cuts...",
                image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=600&auto=format&fit=crop",
                category: "Hair Care",
                slug: "best-hair-salons-abu-dhabi-2025"
              },
              {
                title: "Gel vs Acrylic vs Builder Gel Nails",
                excerpt: "Everything you need to know about choosing the right nail system for the Abu Dhabi climate...",
                image: "https://plus.unsplash.com/premium_photo-1661290231745-15f1ed6fea88?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                category: "Nails",
                slug: "gel-vs-acrylic-vs-builder-gel-guide"
              },
              {
                title: "What is Mesotherapy? A Beginner's Guide",
                excerpt: "Considering mesotherapy but not sure where to start? We break down the process and benefits...",
                image: "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?q=80&w=600&auto=format&fit=crop",
                category: "Skin Treatments",
                slug: "mesotherapy-beginners-guide-abu-dhabi"
              }
            ].map((post, i) => (
              <div 
                key={i} 
                className="group cursor-pointer fade-in-up" 
                onClick={() => onNavigate('blog-post', { slug: post.slug })}
              >
                <div className="aspect-[16/10] overflow-hidden rounded-2xl mb-6 border border-mier/5">
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                  />
                </div>
                <div className="flex items-center gap-3 text-mier/50 text-[10px] uppercase tracking-widest mb-4 font-bold">
                  <span className="text-mier">{post.category}</span>
                </div>
                <h3 className="font-serif text-2xl text-mier group-hover:text-mier-light transition-colors mb-4 leading-tight">
                  {post.title}
                </h3>
                <p className="text-zinc-500 text-sm leading-relaxed line-clamp-2">
                  {post.excerpt}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

